import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import { useTheme } from 'emotion-theming';

import CopyCalendarOption from './CopyCalendarOption';
import CopyCalendarPickerRow from './CopyCalendarPickerRow';
import CopyCheckbox from './CopyCheckbox';
import CopyTargetPill from './CopyTargetPill';
import SimpleCalendarSelectAthleteList from '../SimpleCalendarSelectAthleteList';
import Spinner from '../../../../shared/components/Spinner/Spinner';
import useIsTouchDevice from '../../../../shared/hooks/useIsTouchDevice';
import {
  TabLabel, buttonReset, focusFillLabel, focusRing,
} from '../../../../shared/theme/focusStyles';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

const MAX_VISIBLE_PILLS = 8;
const LOCK_RELEASE_FALLBACK_MS = 300;
const DROPDOWN_ID = 'copy-target-picker-dropdown';
const NO_ATHLETES_FOUND = [{
  id: 'no-athletes-found', first: 'No Athletes Found', last: '', pic: '', fake: true,
}];

const PickerContainer = styled('div')`
  position: relative;
`;

const PillsRow = styled('div')`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const AddButton = styled('button')`
  ${buttonReset}
  padding: 5px 12px;
  border: 1px dashed ${({ theme }) => theme.colors.borderMedium};
  border-radius: 14px;
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text};
  }
  ${focusRing}
`;

const MorePillsButton = styled('button')`
  ${buttonReset}
  padding: 6px 4px;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: ${({ theme }) => theme.colors.link};
  cursor: pointer;
  ${focusRing}
`;

const EmptyHint = styled('div')`
  margin-top: 8px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* On phones the anchored dropdown would be clipped by the modal's scroll
   container, so it drops into the flow as a full-width panel instead. */
const Dropdown = styled('div')`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 20;
  display: flex;
  flex-direction: column;
  width: min(440px, 100%);
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

  @media (max-width: 480px) {
    position: static;
    width: 100%;
    margin-top: 8px;
  }
`;

const TabsRow = styled('div')`
  display: flex;
  margin-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderMedium};
`;

const Tab = styled('button')`
  ${buttonReset}
  margin-bottom: -1px;
  padding: 8px 12px;
  border-bottom: 3px solid ${({ active, theme }) => (active ? theme.colors.link : 'transparent')};
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  color: ${({ active, theme }) => (active ? theme.colors.link : theme.colors.textSecondary)};
  cursor: pointer;
  ${focusFillLabel}
`;

const SearchWrapper = styled('div')`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surfaceInput};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.hover};
  }
`;

const SearchInput = styled('input')`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};

  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const SelectAllRow = styled('div')`
  padding: 4px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const OptionsList = styled('div')`
  overflow-y: auto;
  max-height: 260px;
  padding-top: 4px;

  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background-color: ${({ theme }) => theme.colors.scrollbarThumb};
  }
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colors.scrollbarThumb} transparent;
`;

const ListMessage = styled('div')`
  padding: 8px;
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const toggleInSet = (set, id, isChecked) => {
  const next = new Set(set);
  if (isChecked) {
    next.add(id);
  } else {
    next.delete(id);
  }
  return next;
};

const matchesQuery = (text, query) => (text || '').toLowerCase().includes(query);

const CopyTargetPicker = ({
  isAthleteTarget,
  onTargetTypeChange,
  parentCalendars,
  calendarSelectAthletes,
  isLoading,
  checkedCalendars,
  setCheckedCalendars,
  checkedAthletes,
  setCheckedAthletes,
  onCloseLockChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [pendingPillFocusIndex, setPendingPillFocusIndex] = useState(null);
  const [isAthleteView, setIsAthleteView] = useState(isAthleteTarget);
  const pickerRef = useRef(null);
  const addButtonRef = useRef(null);
  const listRef = useRef(null);
  const pillRemoveRefs = useRef([]);
  const isHoldingLockRef = useRef(false);
  const { colors } = useTheme();
  const isTouchDevice = useIsTouchDevice();

  const query = searchValue.trim().toLowerCase();
  const activeCalendars = parentCalendars
    .filter((calendar) => !calendar.archived)
    .map((calendar) => ({
      ...calendar,
      childCalendars: (calendar.childCalendars || []).filter((child) => !child.archived),
    }));
  const flatCalendars = activeCalendars.flatMap((calendar) => [
    calendar,
    ...calendar.childCalendars.map((child) => ({ ...child, parentName: calendar.name })),
  ]);
  const searchedCalendars = query
    ? flatCalendars.filter((calendar) => matchesQuery(calendar.name, query))
    : [];
  const searchedAthletes = query
    ? calendarSelectAthletes.filter((athlete) => (
      matchesQuery(athlete.first, query) || matchesQuery(athlete.last, query)
    ))
    : [];

  const visibleIds = isAthleteView
    ? (query ? searchedAthletes : calendarSelectAthletes).map((athlete) => athlete.id)
    : (query ? searchedCalendars : flatCalendars).map((calendar) => calendar.id);
  const checkedSet = isAthleteView ? checkedAthletes : checkedCalendars;
  const allVisibleChecked = visibleIds.length > 0 && visibleIds.every((id) => checkedSet.has(id));
  const someVisibleChecked = !allVisibleChecked && visibleIds.some((id) => checkedSet.has(id));

  /* Selecting one kind clears the other, so at most one of these is non-empty. */
  const selectedTargets = [
    ...flatCalendars
      .filter((calendar) => checkedCalendars.has(calendar.id))
      .map((calendar) => ({ id: calendar.id, label: calendar.name, isAthlete: false })),
    ...calendarSelectAthletes
      .filter((athlete) => checkedAthletes.has(athlete.id))
      .map((athlete) => ({ id: athlete.id, label: `${athlete.first} ${athlete.last}`, isAthlete: true })),
  ];
  const visiblePills = selectedTargets.slice(0, MAX_VISIBLE_PILLS);
  const hiddenPillCount = selectedTargets.length - visiblePills.length;
  const searchNoun = isAthleteView ? 'athletes' : 'calendars';

  const closeDropdown = (returnFocus) => {
    setIsOpen(false);
    setSearchValue('');
    if (returnFocus) {
      addButtonRef.current?.focus();
    }
  };

  const handleAddClick = () => {
    if (isOpen) {
      closeDropdown(false);
    } else {
      setIsOpen(true);
    }
  };

  const handleTabClick = (e) => {
    setIsAthleteView(e.currentTarget.value === 'athletes');
    setSearchValue('');
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  /* A copy goes to calendars or to athlete calendars, never both - the API
     rejects a request naming both. So choosing one kind clears the other and
     makes it the copy's target. */
  const claimTargetType = (isAthlete) => {
    onTargetTypeChange(isAthlete);
    if (isAthlete) {
      setCheckedCalendars(new Set());
    } else {
      setCheckedAthletes(new Set());
    }
  };

  const handleToggleCalendar = (id, isChecked) => {
    if (isChecked) {
      claimTargetType(false);
    }
    setCheckedCalendars((prev) => toggleInSet(prev, id, isChecked));
  };

  const handleToggleAthlete = (id, isChecked) => {
    if (isChecked) {
      claimTargetType(true);
    }
    setCheckedAthletes((prev) => toggleInSet(prev, id, isChecked));
  };

  const handleSelectAllChange = () => {
    if (!allVisibleChecked) {
      claimTargetType(isAthleteView);
    }
    const setChecked = isAthleteView ? setCheckedAthletes : setCheckedCalendars;
    setChecked((prev) => {
      const next = new Set(prev);
      visibleIds.forEach((id) => (allVisibleChecked ? next.delete(id) : next.add(id)));
      return next;
    });
  };

  const handleRemovePill = (id) => {
    const index = selectedTargets.findIndex((target) => target.id === id);
    if (selectedTargets[index]?.isAthlete) {
      handleToggleAthlete(id, false);
    } else {
      handleToggleCalendar(id, false);
    }
    setPendingPillFocusIndex(index);
  };

  /* Escape must stop here: react-modal listens for it on an ancestor and would
     close the whole copy modal. The arrow keys walk the checkboxes so a long
     list doesn't need a Tab per chevron and checkbox. */
  const handleDropdownKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closeDropdown(true);
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
      return;
    }
    const checkboxes = Array.from(listRef.current?.querySelectorAll('input[type="checkbox"]') || []);
    if (!checkboxes.length) {
      return;
    }
    const currentIndex = checkboxes.indexOf(document.activeElement);
    if (currentIndex === -1 && e.key === 'ArrowUp') {
      return;
    }
    e.preventDefault();
    const step = e.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = currentIndex === -1
      ? 0
      : Math.min(Math.max(currentIndex + step, 0), checkboxes.length - 1);
    checkboxes[nextIndex].focus();
  };

  /* Pressing a calendar's name (a label, which can't take focus) hands focus to
     the nearest focusable ancestor - react-modal's tabindex="-1" content element.
     That isn't focus leaving the picker, so only a move to a control that doesn't
     contain the picker closes it; outside clicks are the mousedown listener's job. */
  const handlePickerBlur = (e) => {
    const next = e.relatedTarget;
    if (isOpen && next && !pickerRef.current?.contains(next)
      && !next.contains(pickerRef.current)) {
      closeDropdown(false);
    }
  };

  const setPillRemoveRef = (index) => (element) => {
    pillRemoveRefs.current[index] = element;
  };

  /* While the dropdown is open the modal is locked, so a click outside it -
     even on the backdrop - only closes the dropdown. */
  useEffect(() => {
    if (isOpen) {
      onCloseLockChange(true);
    } else if (!isHoldingLockRef.current) {
      onCloseLockChange(false);
    }
  }, [isOpen]);

  useEffect(() => () => onCloseLockChange(false), []);

  /* The dropdown closes on mousedown, but react-modal closes the modal on the
     click that follows it. So the lock is held until that click has been
     handled, then released a tick later. The mouseup fallback covers a press
     that drags off and never becomes a click; it only fires well after any
     click would have. */
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleDocumentMouseDown = (e) => {
      if (pickerRef.current?.contains(e.target)) {
        return;
      }
      isHoldingLockRef.current = true;
      let fallbackTimer = null;
      const listeners = {};
      const releaseLock = () => {
        clearTimeout(fallbackTimer);
        window.removeEventListener('click', listeners.click, true);
        window.removeEventListener('mouseup', listeners.mouseup, true);
        isHoldingLockRef.current = false;
        onCloseLockChange(false);
      };
      listeners.click = () => setTimeout(releaseLock, 0);
      listeners.mouseup = () => {
        fallbackTimer = setTimeout(releaseLock, LOCK_RELEASE_FALLBACK_MS);
      };
      window.addEventListener('click', listeners.click, true);
      window.addEventListener('mouseup', listeners.mouseup, true);
      setIsOpen(false);
      setSearchValue('');
    };
    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown);
  }, [isOpen]);

  /* After a pill's x removes it, focus moves to the pill that slid into its
     place, or to "+ Add" when it was the last one. */
  useEffect(() => {
    if (pendingPillFocusIndex === null) {
      return;
    }
    const nextButton = pillRemoveRefs.current[pendingPillFocusIndex]
      || pillRemoveRefs.current[pendingPillFocusIndex - 1];
    (nextButton || addButtonRef.current)?.focus();
    setPendingPillFocusIndex(null);
  }, [pendingPillFocusIndex]);

  const renderCalendarList = () => {
    if (isLoading) {
      return <Spinner />;
    }
    if (query) {
      return searchedCalendars.length ? searchedCalendars.map((calendar) => (
        <CopyCalendarOption
          calendar={calendar}
          isChecked={checkedCalendars.has(calendar.id)}
          key={calendar.id}
          onToggle={handleToggleCalendar}
          secondaryText={calendar.parentName || ''}
        />
      )) : <ListMessage>No calendars found</ListMessage>;
    }
    return activeCalendars.length ? activeCalendars.map((calendar) => (
      <CopyCalendarPickerRow
        calendar={calendar}
        checkedCalendars={checkedCalendars}
        key={calendar.id}
        onToggleCalendar={handleToggleCalendar}
      />
    )) : <ListMessage>No calendars yet</ListMessage>;
  };

  pillRemoveRefs.current = [];

  return (
    <PickerContainer onBlur={handlePickerBlur} ref={pickerRef}>
      <PillsRow>
        {visiblePills.map((target, index) => (
          <CopyTargetPill
            key={target.id}
            label={target.label}
            onRemove={handleRemovePill}
            removeButtonRef={setPillRemoveRef(index)}
            targetId={target.id}
          />
        ))}
        {hiddenPillCount > 0 && (
          <MorePillsButton onClick={handleAddClick} type='button'>
            {`+${hiddenPillCount} more`}
          </MorePillsButton>
        )}
        <AddButton
          aria-controls={DROPDOWN_ID}
          aria-expanded={isOpen}
          aria-haspopup='true'
          onClick={handleAddClick}
          ref={addButtonRef}
          type='button'
        >
          + Add calendars
        </AddButton>
      </PillsRow>
      {!selectedTargets.length && !isOpen && (
        <EmptyHint>Choose at least one calendar to copy to.</EmptyHint>
      )}

      {isOpen && (
        <Dropdown id={DROPDOWN_ID} onKeyDown={handleDropdownKeyDown}>
          <TabsRow aria-label='Copy to' role='tablist'>
            <Tab
              active={!isAthleteView}
              aria-selected={!isAthleteView}
              onClick={handleTabClick}
              role='tab'
              type='button'
              value='calendars'
            >
              <TabLabel>{`Calendars${checkedCalendars.size ? ` (${checkedCalendars.size})` : ''}`}</TabLabel>
            </Tab>
            <Tab
              active={isAthleteView}
              aria-selected={isAthleteView}
              onClick={handleTabClick}
              role='tab'
              type='button'
              value='athletes'
            >
              <TabLabel>{`Athletes${checkedAthletes.size ? ` (${checkedAthletes.size})` : ''}`}</TabLabel>
            </Tab>
          </TabsRow>

          <SearchWrapper>
            <IcomoonReact aria-hidden='true' color={colors.textSecondary} icon='magnifying-glass' iconSet={iconSet} size={14} />
            <SearchInput
              aria-label={`Search ${searchNoun}`}
              /* Opening the picker is a request to find something, so the search takes
                 focus - except on touch, where that would raise the keyboard over the list. */
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={!isTouchDevice}
              onChange={handleSearchChange}
              placeholder={`Search ${searchNoun}`}
              type='text'
              value={searchValue}
            />
          </SearchWrapper>

          {visibleIds.length > 0 && (
            <SelectAllRow>
              <CopyCheckbox
                checked={allVisibleChecked}
                id='copy-target-select-all'
                indeterminate={someVisibleChecked}
                label={query ? 'Select all results' : `Select all ${searchNoun}`}
                onChange={handleSelectAllChange}
              />
            </SelectAllRow>
          )}

          <OptionsList ref={listRef}>
            {isAthleteView && !query && !calendarSelectAthletes.length && (
              <ListMessage>No athlete calendars yet</ListMessage>
            )}
            {isAthleteView && (query || calendarSelectAthletes.length > 0) && (
              <SimpleCalendarSelectAthleteList
                calendarSelectAthletes={calendarSelectAthletes}
                checkedAthletes={checkedAthletes}
                isUserSearching={!!query}
                onCheckboxChange={handleToggleAthlete}
                searchedAthleteList={searchedAthletes.length ? searchedAthletes : NO_ATHLETES_FOUND}
              />
            )}
            {!isAthleteView && renderCalendarList()}
          </OptionsList>
        </Dropdown>
      )}
    </PickerContainer>
  );
};

CopyTargetPicker.propTypes = {
  isAthleteTarget: PropTypes.bool.isRequired,
  onTargetTypeChange: PropTypes.func.isRequired,
  parentCalendars: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    archived: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    childCalendars: PropTypes.arrayOf(PropTypes.shape({})),
  })).isRequired,
  calendarSelectAthletes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    first: PropTypes.string,
    last: PropTypes.string,
  })).isRequired,
  isLoading: PropTypes.bool,
  /** Called with true while the dropdown is open so the modal won't close. */
  onCloseLockChange: PropTypes.func,
  checkedCalendars: PropTypes.instanceOf(Set).isRequired,
  setCheckedCalendars: PropTypes.func.isRequired,
  checkedAthletes: PropTypes.instanceOf(Set).isRequired,
  setCheckedAthletes: PropTypes.func.isRequired,
};

CopyTargetPicker.defaultProps = {
  isLoading: false,
  onCloseLockChange: () => {},
};

export default CopyTargetPicker;
